import { useDispatch, useSelector } from 'react-redux'
import type { TypedUseSelectorHook } from 'react-redux'
import type { RootState, AppDispatch } from './store'
import { NotificationState, NotificationActions } from './reducers/notification'
import { PageHeaderState, pageHeaderActions } from './reducers/pageHeaderSlice'
import { InfoContentOption } from '../../components/Info/InfoButton'
import dayjs from 'dayjs'
import { monthPickerActions } from './reducers/monthPickerSlice'
import { ProjectDistributionDateRangeActions } from './reducers/dateRangeDistributionSlice'
import { getIsLeader, getIsManagement, getIsProjectLeader } from './reducers/currentUserSlice'
import {
  setLoggingOutWarning,
  setConfirmedLogout,
  setEditMode,
  getIsLoggingOutWarning,
  getIsConfirmedLogout,
  getIsEditMode,
} from './reducers/LoggingOutWarningSlice'
import { useMediaQuery, useTheme } from '@mui/material'
import { useMemo } from 'react'
import { SwapLeadersDTO, useSwapLeadersForProjectsMutation } from './reducers/projectsSlice'
import { Project } from '../types'

export const useAppDispatch: () => AppDispatch = useDispatch
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

export const usePageHeader = () => {
  const dispatch = useDispatch()

  const setHeaderButtons = (buttonSettings: PageHeaderState) => {
    dispatch(pageHeaderActions.setButtons(buttonSettings))
  }

  const setInfoPopupContent = (content: InfoContentOption) => {
    dispatch(pageHeaderActions.setInfoPopupContent(content))
  }

  return { setHeaderButtons, setInfoPopupContent } as const
}

export const useMonthPicker = () => {
  const dispatch = useDispatch()

  const setSelectedDate = (date: dayjs.Dayjs) => {
    dispatch(monthPickerActions.setDate(date.toString()))
  }

  const increaseMonth = () => {
    dispatch(monthPickerActions.increaseMonth())
  }

  const decreaseMonth = () => {
    dispatch(monthPickerActions.decreaseMonth())
  }

  return { setSelectedDate, increaseMonth, decreaseMonth } as const
}

export const useNotification = () => {
  const dispatch = useDispatch()

  const displayNotification = (notification: NotificationState) => {
    dispatch(NotificationActions.addNotification(notification))
  }

  const clearNotification = () => {
    dispatch(NotificationActions.clearNotification())
  }

  return { displayNotification, clearNotification } as const
}

export const useProjectDistributionDateRange = () => {
  const dispatch = useDispatch()

  const setFromDate = (date: string) => {
    dispatch(ProjectDistributionDateRangeActions.setFromDate(date))
  }

  const setToDate = (date: string) => {
    dispatch(ProjectDistributionDateRangeActions.setToDate(date))
  }

  return { setFromDate, setToDate } as const
}

export const useIsOnlyProjectLeader = () => {
  const isManagement = useSelector(getIsManagement)
  const isLeader = useSelector(getIsLeader)
  const isProjectLeader = useSelector(getIsProjectLeader)

  return isProjectLeader && !isManagement && !isLeader
}

export const useLoggingOutWarning = () => {
  const dispatch = useAppDispatch()

  const setIsLoggingOutWarning = (setIsLoggingOutWarning: boolean) => {
    dispatch(setLoggingOutWarning(setIsLoggingOutWarning))
  }

  const isLoggingOutWarning = useAppSelector(getIsLoggingOutWarning)

  return { isLoggingOutWarning, setIsLoggingOutWarning } as const
}

export const useConfirmedLogout = () => {
  const dispatch = useAppDispatch()

  const setIsConfirmedLogout = (status: boolean) => {
    dispatch(setConfirmedLogout(status))
  }

  const isConfirmedLogout = useAppSelector(getIsConfirmedLogout)

  return { isConfirmedLogout, setIsConfirmedLogout }
}

export const useEditMode = () => {
  const dispatch = useAppDispatch()

  const setIsEditMode = (status: boolean) => {
    dispatch(setEditMode(status))
  }

  const isEditMode = useAppSelector(getIsEditMode)

  return { isEditMode, setIsEditMode }
}

export const useSwapLeaders = () => {
  const [swapLeadersForProjects] = useSwapLeadersForProjectsMutation()

  const swapLeaders = async (exLeaderId: string, leaderProjects: Project[], newLeaderId: string): Promise<void> => {
    const swapLeadersDTO: SwapLeadersDTO = {
      projectsIds: leaderProjects.map(project => project.id),
      exLeaderId,
      newLeaderId,
    }
    await swapLeadersForProjects(swapLeadersDTO)
  }

  return { swapLeaders }
}

export const useLeftPadding = () => {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  return useMemo(() => (isSmallScreen ? theme.spacing(2) : theme.spacing(3)), [isSmallScreen, theme])
}
